import {z} from 'zod';
import {STANFORD_LOGO_SRC} from '../../branding/stanfordLogoSrc';
import {isNullOrUndefined} from '../../utils/general';

/**
 * Width, in pixels, of the fixed canvas coordinate space every slide is laid
 * out in. All element `position`/`size` values are in this space, regardless
 * of how large the editor or present-mode viewport actually is on screen —
 * both scale the canvas to fit rather than storing viewport-relative values.
 */
export const CANVAS_WIDTH = 1920;

/** Height, in pixels, of the fixed canvas coordinate space. See {@link CANVAS_WIDTH}. */
export const CANVAS_HEIGHT = 1080;

/**
 * Canvas-space pixels per inch. The canvas represents a 13 1/3in x 7.5in
 * slide (standard 16:9 PowerPoint dimensions) rendered at 144dpi, so
 * `CANVAS_WIDTH / (40 / 3) === 144` exactly.
 */
export const CANVAS_PX_PER_INCH = 144;

/** A 2D point in canvas space (see {@link CANVAS_WIDTH}), in pixels. */
export const PositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

/** A width/height pair in canvas-space pixels. Both must be positive. */
export const SizeSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
});

/**
 * Entrance effects an element can play when its build step is reached in
 * present mode. `'appear'` is an instant hard cut to visible — no fade,
 * scale, or shift, matching PowerPoint's own "Appear" entrance. `'write'`
 * self-draws a `freehand` shape's stroke — the polyline extends from its
 * first point to its last as if drawn by hand (see
 * `shared/ElementContent.tsx`'s `drawOnReveal`); for every other element
 * type it currently falls back to a plain fade, same as `'fade'` (see
 * `shared/elementMotion.ts` for why it no longer does a clip-path reveal
 * wipe on text/equations).
 */
export const ELEMENT_ENTER_ANIMATIONS = [
  'appear',
  'fade',
  'write',
  'grow',
  'shift-up',
  'shift-down',
  'shift-left',
  'shift-right',
] as const;

/** One of the registered {@link ELEMENT_ENTER_ANIMATIONS}. */
export const ElementEnterAnimationSchema = z.enum(ELEMENT_ENTER_ANIMATIONS);

/**
 * How fast a `'write'` entrance draws itself (see
 * {@link AnimationSchema.writeSpeed}). Scales the per-character / per-glyph
 * cadence for text and equations, and the pen speed for a freehand stroke, via
 * a multiplier in `shared/enterDuration.ts` — both the actual playback and the
 * duration present mode predicts for `'after-previous'` chaining read that one
 * multiplier, so they stay in lockstep. `'normal'` is the default (and the
 * historical, pre-option behavior); `'slow'` lingers, `'fast'` rushes. Only
 * meaningful for `enterType: 'write'`; ignored by every other entrance.
 */
export const WRITE_SPEEDS = ['slow', 'normal', 'fast'] as const;

/** One of the registered {@link WRITE_SPEEDS}. */
export const WriteSpeedSchema = z.enum(WRITE_SPEEDS);

/** How fast a `'write'` entrance draws. See {@link WriteSpeedSchema}. */
export type WriteSpeed = z.infer<typeof WriteSpeedSchema>;

/**
 * How fast a `'fade'` entrance plays (see {@link AnimationSchema.fadeSpeed}).
 * Scales `shared/enterDuration.ts`'s `ENTER_DURATION_SEC` via a multiplier —
 * both the actual `framer-motion` transition (`shared/elementMotion.ts`) and
 * the duration present mode predicts for `'after-previous'` chaining read
 * that one multiplier, so they stay in lockstep. `'normal'` is the default
 * (and the historical, pre-option behavior); `'slow'` lingers, `'fast'`
 * rushes, `'very-fast'` is near-instant. Only meaningful for
 * `enterType: 'fade'`; ignored by every other entrance.
 */
export const FADE_SPEEDS = ['slow', 'normal', 'fast', 'very-fast'] as const;

/** One of the registered {@link FADE_SPEEDS}. */
export const FadeSpeedSchema = z.enum(FADE_SPEEDS);

/** How fast a `'fade'` entrance plays. See {@link FadeSpeedSchema}. */
export type FadeSpeed = z.infer<typeof FadeSpeedSchema>;

/** A canvas-space bounding box, in pixels. See {@link GlyphTokenSchema}. */
export const GlyphRectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

/**
 * One measured glyph from a rendered LaTeX expression, as extracted by
 * `shared/glyphTokens.ts`'s `extractGlyphTokens` and cached in
 * {@link MorphPlanSchema}. `className` is the glyph's KaTeX class list
 * (e.g. `"mord mathnormal"`), captured so the glyph can be re-rendered
 * later as `<span className={token.className}>{token.key}</span>` with
 * the right font/italics, without keeping a live DOM reference.
 */
export const GlyphTokenSchema = z.object({
  key: z.string(),
  rect: GlyphRectSchema,
  className: z.string().optional(),
  /**
   * The glyph's resolved font size in canvas px (from `getComputedStyle`,
   * which CSS transforms don't affect — so it's the same number in the live
   * canvas and the plan-sync sandbox). KaTeX sizes sub/superscripts via
   * ancestor `reset-size…`/`size…` wrapper classes that `className` (the leaf's
   * own classes) doesn't carry, so without this a script-level glyph would
   * re-render at the element's full base size during a morph. Note this
   * includes KaTeX's own `.katex` em scaling on top of the element's base font
   * size. Omitted for visual atoms (`html` tokens), whose size is box-driven.
   */
  fontSize: z.number().optional(),
  /**
   * The glyph's own resolved CSS color (from `getComputedStyle`), captured so a
   * sub-part recolored with `\textcolor` (see `MorphTextStateSchema`) animates
   * in *its* color during a morph rather than snapping from the element's base
   * color only once the transition settles. Omitted when it equals the side's
   * base color (the common, uncolored case), which keeps cached plans small;
   * consumers fall back to the base color then.
   */
  color: z.string().optional(),
  /**
   * Verbatim HTML for a non-text KaTeX visual atom that has no character of its
   * own — a radical sign / stretchy-delimiter `<svg>` or a fraction bar
   * (`.frac-line`). Present only for such atoms; a normal character glyph omits
   * it and is reconstructed from `key` + `className` instead. Captured so these
   * marks glide/persist through a morph rather than vanishing mid-transition
   * (the glyph reconstruction can't draw an SVG). See `shared/glyphTokens.ts`.
   */
  html: z.string().optional(),
});

/**
 * Precomputed glyph-morph plan for a `morphFrom` pair where both sides
 * have a LaTeX representation (see `shared/elementToLatex.ts`) — computed
 * by the editor (`editor/useMorphPlanSync.ts`) and consumed by present
 * mode (`shared/GlyphMorphOverlay.tsx`), so the DOM measurement pass runs
 * once at authoring time rather than live during a presentation.
 * `fingerprint` is a plain string built from every input that affects the
 * computed tokens (source/target latex, fontSize, position, size) — not a
 * cryptographic hash, just cheap to compare so the editor can tell a plan
 * is stale and needs recomputing.
 */
export const MorphPlanSchema = z.object({
  fingerprint: z.string(),
  sourceTokens: z.array(GlyphTokenSchema),
  targetTokens: z.array(GlyphTokenSchema),
  /**
   * Index pairs into `sourceTokens`/`targetTokens` for glyphs that morph
   * into each other. An object, not a `[sourceIndex, targetIndex]` tuple —
   * a tuple serializes as a nested array, which Firestore's `setDoc`
   * rejects outright ("Nested arrays are not supported"); since this
   * whole plan lives inside one array element of the deck document, that
   * failure doesn't just drop the morph plan, it fails the *entire* save,
   * silently blocking every other pending edit too.
   */
  matchedIndexes: z.array(
    z.object({
      sourceIndex: z.number().int().nonnegative(),
      targetIndex: z.number().int().nonnegative(),
    }),
  ),
  /** Indexes into `sourceTokens` for glyphs with no match — fade out in place. */
  removedIndexes: z.array(z.number().int().nonnegative()),
  /** Indexes into `targetTokens` for glyphs with no match — fade in in place. */
  addedIndexes: z.array(z.number().int().nonnegative()),
});

/**
 * Which color-bearing field of an element a {@link ColorChangeSchema} entry
 * retargets. Not every element type exposes every one of these — e.g. a
 * `text` element has only `'color'`, a `'dice'` shape only `'fillColor'` —
 * the editor scopes the picker to whichever properties the selected element
 * actually has (see `editor/animationMeta.tsx`'s `getElementColorProperties`);
 * this schema itself doesn't restrict the pairing.
 */
export const COLOR_CHANGE_PROPERTIES = [
  'fillColor',
  'borderColor',
  'color',
] as const;

/** One of the registered {@link COLOR_CHANGE_PROPERTIES}. */
export const ColorChangePropertySchema = z.enum(COLOR_CHANGE_PROPERTIES);

/** A color-bearing element field a color-change animation can retarget. See {@link ColorChangePropertySchema}. */
export type ColorChangeProperty = z.infer<typeof ColorChangePropertySchema>;

/**
 * A build-step color-change animation: at `step`, `property` tweens from
 * whatever it was rendering (its own static value, or the previous
 * color-change's `toColor`) to `toColor`. Unlike an entrance (see
 * {@link AnimationSchema.enterType}), this targets an element that's already
 * visible — a die's face changing color when it settles, a status shape
 * flipping from red to green — so it's a separate list rather than a variant
 * of the entrance enum. An element can carry several, at the same or
 * different steps, on the same or different properties; see
 * `shared/animationVisibility.ts`'s `getElementColorAtStep` for how they're
 * resolved for a given build step, and `shared/enterDuration.ts`'s
 * `getColorChangeTransitionSec` for how long the tween plays.
 */
export const ColorChangeSchema = z.object({
  /** Build step (0-indexed) at which this color change plays. */
  step: z.number().int().nonnegative(),
  /** Which color-bearing field this entry retargets. See {@link ColorChangePropertySchema}. */
  property: ColorChangePropertySchema,
  /** CSS color string to tween into. */
  toColor: z.string(),
  /**
   * How fast the tween plays, reusing the entrance system's
   * {@link FadeSpeedSchema} presets rather than a bespoke speed enum, since a
   * color tween is conceptually the same "how long does this take" choice as
   * a `'fade'` entrance. Unset means `'normal'`.
   */
  speed: FadeSpeedSchema.optional(),
});

/** A build-step color-change animation. See {@link ColorChangeSchema}. */
export type ColorChange = z.infer<typeof ColorChangeSchema>;

/**
 * Build-step animation config for a slide element. `enterStep`/`enterType`
 * and `morphFrom` are independent and composable — an element can have
 * either, both, or neither of the two "how do I enter" mechanisms:
 *
 * - `enterType` (optional): a self-contained entrance effect (fade, grow,
 *   shift, ...) playing on this element alone.
 * - `morphFrom` (optional): the id of another element on the *same slide*
 *   that this element visually morphs in from. Only takes effect when
 *   both sides have a LaTeX representation (see `shared/elementToLatex.ts`:
 *   `equation` directly, `text` via `shared/textToLatex.ts`), in which
 *   case it plays a per-glyph transition matching manim's
 *   `TransformMatchingTex`: shared characters glide from their old
 *   position/size to their new one, changed characters fade in/out (see
 *   `shared/GlyphMorphOverlay.tsx`, `morphPlan` below). Otherwise (e.g. an
 *   image, table, or component on either side) it has no effect at all —
 *   the linked element just appears/disappears per its own `enterType`/
 *   build step, with no transition between the two.
 *
 * Setting `morphFrom` needs no reciprocal config on the source element —
 * the source's exit is *implicit*: `shared/animationVisibility.ts` derives
 * it by scanning the slide for any element whose `morphFrom` points back
 * at it, and treats that as "I disappear the moment my morph target
 * enters." There is currently no way to make an element disappear except
 * by being the source of a morph.
 */
export const AnimationSchema = z.object({
  /** Build step (0-indexed) at which this element becomes visible. 0 means visible immediately, with no click needed. */
  enterStep: z.number().int().nonnegative(),
  /** Self-contained entrance effect played when `enterStep` is reached. Omit to rely solely on `morphFrom`'s transition (or to just appear with no animation, if `morphFrom` is also unset). */
  enterType: ElementEnterAnimationSchema.optional(),
  /** How fast a `'write'` entrance draws (see {@link WriteSpeedSchema}). Unset means `'normal'`. Ignored for every non-`'write'` `enterType`. */
  writeSpeed: WriteSpeedSchema.optional(),
  /** How fast a `'fade'` entrance plays (see {@link FadeSpeedSchema}). Unset means `'normal'`. Ignored for every non-`'fade'` `enterType`. */
  fadeSpeed: FadeSpeedSchema.optional(),
  /** Id of another element on the same slide this element morphs in from. See this schema's doc comment. */
  morphFrom: z.string().optional(),
  /** Precomputed glyph-morph plan for `morphFrom`, when applicable. See {@link MorphPlanSchema}. Absent or stale (see `fingerprint`) falls back to computing it live. */
  morphPlan: MorphPlanSchema.optional(),
  /**
   * Build-step color-change animations for this element (see
   * {@link ColorChangeSchema}) — independent of `enterStep`/`enterType`, since
   * a color change targets an element that may already be visible. Unset or
   * empty means no color-change animations, the historical default. Order is
   * not significant; entries are resolved per-property in step order at read
   * time (see `shared/animationVisibility.ts`'s `getElementColorAtStep`).
   */
  colorChanges: z.array(ColorChangeSchema).optional(),
});

/**
 * How the presenter reaches a build step in present mode — the transition
 * *into* a step (see {@link SlideSchema.stepTriggers}):
 *
 * - `'on-click'`: the step waits for the presenter to advance (space/→), like
 *   a clicker. The default for every step.
 * - `'after-previous'`: the step plays on its own the moment every element in
 *   the *previous* step has finished animating — no click. It chains, so
 *   several consecutive `'after-previous'` steps play one after another off a
 *   single click into the first of them. See `present/SlidesPresentPage.tsx`'s
 *   auto-advance, and `shared/enterDuration.ts` for how the wait is timed.
 *
 * Step 0 is the slide's initial state (shown on arrival) and has no trigger.
 */
export const STEP_START_TRIGGERS = ['on-click', 'after-previous'] as const;

/** One of the registered {@link STEP_START_TRIGGERS}. */
export const StepStartTriggerSchema = z.enum(STEP_START_TRIGGERS);

/** How a build step begins in present mode. See {@link StepStartTriggerSchema}. */
export type StepStartTrigger = z.infer<typeof StepStartTriggerSchema>;

/** Build-step animation config for a slide element. See {@link AnimationSchema}. */
export type Animation = z.infer<typeof AnimationSchema>;
/** An entrance effect. See {@link ElementEnterAnimationSchema}. */
export type ElementEnterAnimation = z.infer<typeof ElementEnterAnimationSchema>;
/** A canvas-space bounding box. See {@link GlyphRectSchema}. */
export type GlyphRect = z.infer<typeof GlyphRectSchema>;
/** One measured glyph. See {@link GlyphTokenSchema}. */
export type GlyphToken = z.infer<typeof GlyphTokenSchema>;
/** A precomputed glyph-morph plan. See {@link MorphPlanSchema}. */
export type MorphPlan = z.infer<typeof MorphPlanSchema>;

/** Fields shared by every slide element, regardless of {@link ElementSchema} variant. */
const BaseElementSchema = z.object({
  /** Stable identifier, unique within the deck. Generated via `crypto.randomUUID()`. */
  id: z.string(),
  /** Top-left corner of the element's bounding box, in canvas-space pixels. */
  position: PositionSchema,
  /** Size of the element's bounding box, in canvas-space pixels. */
  size: SizeSchema,
  /** Optional build-step animation config. Undefined means the element is always visible from the start, with no animation. */
  animation: AnimationSchema.optional(),
  /**
   * Id of the group this element belongs to (PowerPoint/Google-Slides style
   * grouping). Every element sharing the same `groupId` forms one group that
   * selects, moves, deletes, duplicates, and restacks together — see
   * `shared/grouping.ts` and `data/useDeckEditor.ts`. The value is minted with
   * `crypto.randomUUID()` by the group action; it is *not* an element id.
   *
   * Absent means the element is ungrouped — the historical behavior, so every
   * deck saved before grouping existed keeps loading and behaving exactly as
   * before (migrate-on-read: an absent key is simply left absent). Ungrouping
   * drops the key rather than writing `undefined` (Firestore's `setDoc`
   * rejects `undefined`), matching the optional-field convention used by
   * `SlideSchema.hidden`/`hideMaster`. The `elements` array stays flat — there
   * is deliberately no nested/container element type.
   */
  groupId: z.string().optional(),
});

/**
 * Rich text content for a {@link TextElementSchema}: a TipTap/ProseMirror JSON
 * document (`json`), stored opaquely, plus a plain-text projection
 * (`plainText`) for contexts that can't render rich HTML. `json` is
 * interpreted against the extension schema in `shared/textExtensions.ts` —
 * both the live editor and the read-only `generateHTML` renderer use that
 * same list, so what's typed always matches what's displayed.
 */
export const RichTextContentSchema = z.object({
  json: z.record(z.string(), z.unknown()),
  plainText: z.string(),
});

/**
 * A text box. `fontSize` is independent of `size` (the bounding box) — resizing
 * the box does not change the font size, and vice versa.
 */
export const TextElementSchema = BaseElementSchema.extend({
  type: z.literal('text'),
  content: RichTextContentSchema,
  /** Font size in slide points (pt), matching PowerPoint font sizes — NOT canvas pixels; rendered at 2 canvas px per pt (see `utils/slideUnits.ts`). Must be positive. */
  fontSize: z.number().positive(),
  /** CSS color string for the text. Falls back to the deck master's default element color ({@link SlideMasterSchema.elementColor}) when unset. */
  color: z.string().optional(),
  /**
   * Left tab-stop positions, in canvas-space pixels measured from the text
   * box's left content edge, used by the editor's ruler (see
   * `editor/TextRuler.tsx`). Each `tab` node in `content` (inserted with the
   * Tab key — see `shared/TabStop.ts`) advances the caret to the first stop
   * strictly to the right of its current x; content past the last stop falls
   * back to a fixed default advance. Stored sorted ascending. Unset or empty
   * means no custom stops (tabs use the default advance everywhere), so old
   * decks and boxes that never touched the ruler are unaffected. Only left
   * stops exist today; center/right/decimal are future additions.
   */
  tabStops: z.array(z.number().nonnegative()).optional(),
});

/**
 * A non-destructive crop applied to an {@link ImageElementSchema}, stored as
 * the kept sub-rectangle of the *source* image in normalized coordinates —
 * fractions of the source's natural width/height — so it is resolution- and
 * display-size-independent and the original `src` is never re-encoded.
 *
 * The kept region is the axis-aligned rectangle with top-left corner
 * (`x`, `y`) and size `width` x `height`: the closed interval `[x, x + width]`
 * horizontally and `[y, y + height]` vertically, all within the unit square
 * `[0, 1]` x `[0, 1]` (`x + width` and `y + height` never exceed 1). When
 * present, only this region is displayed, stretched to fill the element's box
 * (see `shared/SlideImage.tsx`); an absent crop — the default for every image
 * — shows the whole picture, and `{x: 0, y: 0, width: 1, height: 1}` is the
 * equivalent explicit "no crop".
 */
/**
 * The shape masks an image can be cropped to (see
 * {@link ImageElementSchema.maskShape}). `'circle'` fills the box as an
 * ellipse; `'roundedRect'` rounds the box's corners by
 * {@link DEFAULT_ROUNDED_RECT_RADIUS}. Only these two are supported for now —
 * more (star, heart, etc.) can be added to this list later without a schema
 * migration.
 */
export const IMAGE_MASK_SHAPES = ['circle', 'roundedRect'] as const;

/** One of the registered {@link IMAGE_MASK_SHAPES}. */
export const ImageMaskShapeSchema = z.enum(IMAGE_MASK_SHAPES);

/** A shape mask applied to an image. See {@link ImageMaskShapeSchema}. */
export type ImageMaskShape = z.infer<typeof ImageMaskShapeSchema>;

export const ImageCropSchema = z.object({
  /** Left edge of the kept region, as a fraction of source width. In `[0, 1]`. */
  x: z.number().min(0).max(1),
  /** Top edge of the kept region, as a fraction of source height. In `[0, 1]`. */
  y: z.number().min(0).max(1),
  /** Width of the kept region, as a fraction of source width. In `(0, 1]`; `x + width <= 1`. Zero is rejected — the editor divides by it. */
  width: z.number().gt(0).max(1),
  /** Height of the kept region, as a fraction of source height. In `(0, 1]`; `y + height <= 1`. Zero is rejected — the editor divides by it. */
  height: z.number().gt(0).max(1),
});

/**
 * An image element. `src` is normally a Firebase Storage download URL
 * (resolved once at upload time via `getDownloadURL`, see
 * `data/useDeckEditor.ts`) for an object under
 * `slides/{courseId}/{slideProjectId}/assets/` (see `data/slideDeckPaths.ts`)
 * — never a data URL. The one exception is the default Stanford seal logo
 * (see {@link createMasterLogoElement}), whose `src` is a static path served
 * from the webapp's `public/` directory rather than an uploaded asset.
 */
export const ImageElementSchema = BaseElementSchema.extend({
  type: z.literal('image'),
  src: z.string(),
  /** Optional alt text for accessibility. */
  alt: z.string().optional(),
  /**
   * Optional non-destructive crop (see {@link ImageCropSchema}). Absent means
   * the whole image is shown — the default for every image, so existing decks
   * are unchanged (migrate on read). Re-editable: the editor's crop mode reads
   * this back to seed the current region.
   */
  crop: ImageCropSchema.optional(),
  /**
   * Optional shape mask (see {@link ImageMaskShapeSchema}), applied on top of
   * any {@link ImageElementSchema.crop} — the rectangular crop still selects
   * which pixels show, and the mask then clips that result to a circle or
   * rounded rectangle (see `shared/SlideImage.tsx`). Absent (the default)
   * shows the full rectangular box.
   */
  maskShape: ImageMaskShapeSchema.optional(),
});

/**
 * When a video element starts playing in present mode:
 *
 * - `'manual'`: never on a build beat — the presenter starts it with the
 *   video's own native controls (click its play button). The default for an
 *   unset {@link VideoElementSchema.playTrigger}.
 * - `'on-click'`: the video shows its first frame and waits; it starts
 *   playing when the presenter advances a build step. An on-click start
 *   therefore consumes one build step — by default `enterStep + 1` (see
 *   `shared/animationVisibility.ts`'s `getElementPlaySteps`), but freely
 *   repositionable anywhere later in the build via
 *   {@link VideoElementSchema.playStep} — and appears as an ordinary,
 *   drag-reorderable "Play" row in the editor's animation pane, mirroring a
 *   demo's {@link SlideComponentPlayTriggerSchema}.
 * - `'on-enter'`: plays as soon as the element appears (its `enterStep` is
 *   reached, or immediately on slide arrival for an unanimated element).
 *
 * Edited via the ribbon's "Play" control while a video is selected. Stepping
 * back before the play beat pauses the video and rewinds it to its first
 * frame (see `shared/SlideVideo.tsx`).
 */
export const VIDEO_PLAY_TRIGGERS = ['manual', 'on-click', 'on-enter'] as const;

/** One of the registered {@link VIDEO_PLAY_TRIGGERS}. */
export const VideoPlayTriggerSchema = z.enum(VIDEO_PLAY_TRIGGERS);

/** When a video starts playing. See {@link VideoPlayTriggerSchema}. */
export type VideoPlayTrigger = z.infer<typeof VideoPlayTriggerSchema>;

/**
 * A video element (e.g. an mp4 clip). `src` is a Firebase Storage download
 * URL for an object under `slides/{courseId}/{slideProjectId}/assets/` —
 * uploaded exactly like an {@link ImageElementSchema}'s image (see
 * `data/useDeckEditor.ts`). In the editor it renders as its static first
 * frame (draggable/resizable like an image); in present mode it renders with
 * native playback controls, and playback can additionally be sequenced into
 * the slide's build via {@link VideoElementSchema.playTrigger}.
 */
export const VideoElementSchema = BaseElementSchema.extend({
  type: z.literal('video'),
  src: z.string(),
  /** Optional accessible label (the `<video>` has no alt attribute; rendered as `aria-label`). */
  alt: z.string().optional(),
  /**
   * When playback starts in present mode. See {@link VideoPlayTriggerSchema}.
   * Unset is treated as `'manual'` (native controls only, no build beat); the
   * editor drops the key rather than storing the default (Firestore rejects
   * `undefined`, and no redundant value is persisted).
   */
  playTrigger: VideoPlayTriggerSchema.optional(),
  /**
   * Explicit build step for this video's on-click play beat. See
   * {@link VideoPlayTriggerSchema}. Only meaningful when `playTrigger ===
   * 'on-click'`; ignored (and dropped at write time) otherwise.
   *
   * Unset means the historical default: the step immediately after the
   * entrance step. When set, it must be strictly after the entrance step; a
   * stale value (e.g. after the entrance was dragged past it) falls back to
   * the default at read time — see `shared/animationVisibility.ts`'s
   * `getElementPlaySteps`, the only reader. Maintained by the animation
   * pane's drag-to-reorder (`shared/animationSequence.ts`), mirroring
   * {@link ComponentElementSchema.playStep}.
   */
  playStep: z.number().int().nonnegative().optional(),
});

/**
 * A block LaTeX equation, rendered via KaTeX in display mode (see
 * `shared/ElementContent.tsx`). `fontSize` behaves exactly like
 * {@link TextElementSchema.fontSize} — independent of the bounding box,
 * shared with the toolbar's font-size control.
 */
export const EquationElementSchema = BaseElementSchema.extend({
  type: z.literal('equation'),
  /** Raw LaTeX source (no surrounding `$$` delimiters). */
  latex: z.string(),
  /** Font size in slide points (pt), matching PowerPoint font sizes — NOT canvas pixels; rendered at 2 canvas px per pt (see `utils/slideUnits.ts`). Must be positive. */
  fontSize: z.number().positive(),
  /** CSS color string for the rendered equation. Falls back to the deck master's default element color ({@link SlideMasterSchema.elementColor}) when unset. */
  color: z.string().optional(),
});

/**
 * One state (keyframe) of a {@link MorphingTextElementSchema}: a LaTeX
 * snapshot shown at one build step, plus an optional precomputed glyph-morph
 * plan for the transition *into* this state from the previous one.
 *
 * Per-part coloring is expressed inside `latex` itself via KaTeX's
 * `\textcolor{#hex}{...}` (the sidebar inserts it — see
 * `editor/MorphingTextPane.tsx`), so a state needs no separate color field
 * beyond the element-level {@link MorphingTextElementSchema.color} default.
 */
export const MorphTextStateSchema = z.object({
  /**
   * Raw LaTeX source for this state (no surrounding `$$`). May embed
   * `\textcolor{#hex}{...}` to recolor individual sub-parts for this state.
   * May also use `&` as an alignment anchor (equations render inside an
   * injected `aligned` environment — see `shared/elementToLatex.ts`'s
   * `wrapEquationForAlignment`): when any state of the element contains a
   * `&`, the whole sequence anchors instead of centering — horizontally
   * (left-anchored in the box) *and* vertically (every state's baseline is
   * pinned to state 0's — see `shared/glyphTokens.ts`'s
   * `getMorphStateVerticalOffset`) — so a shared prefix (e.g.
   * `\text{SE} &= ...`) holds perfectly still while the right-hand side
   * morphs and the equation grows/shrinks around the anchor row.
   */
  latex: z.string(),
  /**
   * Precomputed glyph-morph plan for the transition *from the previous state
   * into this one* (see {@link MorphPlanSchema}), filled in off-screen by
   * `editor/useMorphPlanSync.ts` so present mode never measures DOM live.
   * Unused on the first state (index 0), which has no incoming transition.
   * Absent or stale (fingerprint mismatch) falls back to live measurement.
   */
  morphPlan: MorphPlanSchema.optional(),
});

/**
 * A "morphing text" element: one box holding an ordered list of LaTeX
 * `states` (keyframes), each revealed at a successive build step. Advancing
 * a step in present mode morphs the current state into the next one via the
 * same glyph-level engine as `morphFrom` (see `shared/GlyphMorphOverlay.tsx`):
 * shared characters glide from their old position/size to their new one,
 * changed characters fade in/out. The whole sequence occupies a contiguous
 * range of build steps starting at `animation.enterStep` — state `i` is shown
 * from step `enterStep + i` onward (see `shared/animationVisibility.ts`'s
 * `getMorphingTextStateAtStep`). Authored through the keyframe sidebar in
 * `editor/MorphingTextPane.tsx`; `fontSize`/`color` behave exactly like
 * {@link EquationElementSchema}'s (independent of the bounding box).
 */
export const MorphingTextElementSchema = BaseElementSchema.extend({
  type: z.literal('morphing-text'),
  /**
   * Ordered list of states (keyframes); always at least one. The presenter
   * advances one state per build step, playing a glyph morph between
   * consecutive states — by default on consecutive steps starting at the
   * element's entrance, or wherever {@link stateSteps} places them.
   */
  states: z.array(MorphTextStateSchema).min(1),
  /** Font size in slide points (pt), matching PowerPoint font sizes — NOT canvas pixels; rendered at 2 canvas px per pt (see `utils/slideUnits.ts`). Must be positive. */
  fontSize: z.number().positive(),
  /**
   * CSS color string for the rendered equation. Falls back to the deck
   * master's default element color ({@link SlideMasterSchema.elementColor})
   * when unset. Individual sub-parts of a given state can override it via
   * `\textcolor` embedded in that state's `latex`.
   */
  color: z.string().optional(),
  /**
   * Explicit build steps for this element's state transitions, letting other
   * elements' entrances interleave with (or share a click with) the
   * state-by-state reveal. Entry `i` is the build step at which state `i + 1`
   * begins — state 0 always shows from the element's entrance step, so it has
   * no entry here. The same shape and repair-on-read philosophy as
   * `ComponentElementSchema.phaseSteps`: unset means the historical default
   * (consecutive steps from the entrance); when set, steps must be strictly
   * increasing and after the entrance step; stale entries (e.g. after a state
   * is added/removed/reordered in `editor/MorphingTextPane.tsx`) are repaired
   * at read time — see `shared/animationVisibility.ts`'s
   * `getMorphingTextStateSteps`, the only reader. Maintained by the animation
   * pane's drag-to-reorder (`shared/animationSequence.ts`).
   */
  stateSteps: z.array(z.number().int().nonnegative()).optional(),
});

/**
 * A table's border rendering, applied via `shared/slideTable.css`:
 *
 * - `'booktabs'`: a thick rule above/below the whole table and a thin rule
 *   below the first row, no other grid lines (the clean "booktabs" look from
 *   typography/LaTeX). This is the default for unset or unrecognized styles.
 * - `'grid'`: every cell fully bordered, like a spreadsheet.
 * - `'none'`: no borders at all.
 */
export const TABLE_BORDER_STYLES = ['booktabs', 'grid', 'none'] as const;

/** One of the registered {@link TABLE_BORDER_STYLES}. */
export const TableBorderStyleSchema = z.enum(TABLE_BORDER_STYLES);

/** A table's border rendering. See {@link TableBorderStyleSchema}. */
export type TableBorderStyle = z.infer<typeof TableBorderStyleSchema>;

/**
 * The border style used when a table has none set, or has a stored value we no
 * longer recognize (e.g. the legacy `'lines'`, renamed to `'booktabs'`).
 */
export const DEFAULT_TABLE_BORDER_STYLE: TableBorderStyle = 'booktabs';

/**
 * Row-by-row entrance effects a {@link TableElementSchema} can play instead of
 * revealing as a single unit — see {@link TableElementSchema.rowReveal}:
 *
 * - `'appear'`: each row is a hard cut to visible, no transition.
 * - `'write'`: each row writes itself on character-by-character, the same
 *   look/cadence as a `write` text entrance (see `shared/TextWriteReveal.tsx`).
 * - `'fade'`: each row fades in (opacity tween).
 */
export const TABLE_ROW_REVEAL_TYPES = ['appear', 'write', 'fade'] as const;

/** One of the registered {@link TABLE_ROW_REVEAL_TYPES}. */
export const TableRowRevealTypeSchema = z.enum(TABLE_ROW_REVEAL_TYPES);

/** A table row-by-row reveal effect. See {@link TableRowRevealTypeSchema}. */
export type TableRowRevealType = z.infer<typeof TableRowRevealTypeSchema>;

/**
 * A table element. `content` is a TipTap/ProseMirror JSON document (same
 * shape as {@link TextElementSchema.content}) whose root contains a single
 * `table` node built from the extensions in `shared/tableExtensions.ts` —
 * each cell holds its own paragraph content, so cells support the same rich
 * text (bold/italic/lists/inline math) as a regular text element, and
 * row/column structure is edited via TipTap's table commands rather than a
 * bespoke grid data structure.
 */
export const TableElementSchema = BaseElementSchema.extend({
  type: z.literal('table'),
  content: RichTextContentSchema,
  /** Font size in slide points (pt) for cell text, matching PowerPoint font sizes — NOT canvas pixels; rendered at 2 canvas px per pt (see `utils/slideUnits.ts`). Must be positive. */
  fontSize: z.number().positive(),
  /** CSS color string for cell text (and, on the canvas, the table outline). Falls back to the deck master's default element color ({@link SlideMasterSchema.elementColor}) when unset. */
  color: z.string().optional(),
  /**
   * The table's border rendering. Unset falls back to
   * {@link DEFAULT_TABLE_BORDER_STYLE} at render time; a stored value we don't
   * recognize (e.g. the legacy `'lines'`) is coerced to that default here via
   * `.catch`, so old decks keep loading after a style is renamed or removed.
   */
  borderStyle: TableBorderStyleSchema.optional().catch(
    DEFAULT_TABLE_BORDER_STYLE,
  ),
  /**
   * When set, this table reveals its rows one at a time in present mode
   * instead of as one unit — each row becomes visible on its own build step
   * starting at `animation.enterStep` (row 0 at `enterStep`, row 1 at
   * `enterStep + 1`, ... by default, or wherever {@link rowSteps} places
   * them; see `shared/animationVisibility.ts`'s `getTableRowSteps`), playing
   * this effect. Independent of the table's own `animation.enterType`, which
   * still governs how the table's box itself appears — rows are individually
   * hidden until their own step regardless of (or absence of) a box-level
   * entrance. Unset (the default) means the table reveals as a single unit,
   * the historical behavior.
   */
  rowReveal: TableRowRevealTypeSchema.optional(),
  /**
   * Explicit build steps for a `rowReveal` table's row-by-row transitions,
   * letting other elements' entrances interleave with (or share a click
   * with) the row-by-row reveal. Entry `i` is the build step at which row
   * `i + 1` begins — row 0 always shows from the element's entrance step, so
   * it has no entry here. The same shape and repair-on-read philosophy as
   * `ComponentElementSchema.phaseSteps`/`MorphingTextElementSchema.stateSteps`:
   * unset means the historical default (consecutive steps from the
   * entrance); when set, steps must be strictly increasing and after the
   * entrance step; stale entries (e.g. after a row is added/removed by
   * editing the table) are repaired at read time — see
   * `shared/animationVisibility.ts`'s `getTableRowSteps`, the only reader.
   * Maintained by the animation pane's drag-to-reorder
   * (`shared/animationSequence.ts`). Ignored when `rowReveal` is unset.
   */
  rowSteps: z.array(z.number().int().nonnegative()).optional(),
});

/**
 * Registry of embeddable interactive React components, keyed by id. Each id
 * must have a matching entry in `shared/componentRegistry.tsx` (webapp) —
 * that file, not this one, owns the actual component implementations, since
 * `deck.ts` stays framework/render-agnostic. See `demos/README.md` (webapp)
 * for how to add a new one.
 */
export const SLIDE_COMPONENT_IDS = [
  'llm-demo',
  'llm-animation-demo',
  'llm-internals-demo',
  'dice-demo',
  'ucb-demo',
  'weather-demo',
  'weather-condition-demo',
  'letters-demo',
  'freethrow-demo',
  'netflix-demo',
  'code-count-demo',
  'sample-size-demo',
  'word-likelihood-demo',
  'word-probability-demo',
  'off-by-one-demo',
  'bayes-demo',
  'eikosogram-demo',
  'monty-hall-demo',
  'tree-demo',
  'simpson-demo',
  'venn-demo',
  'galton-demo',
  'binomial-calculator-demo',
  'bernoulli-review-demo',
  'binomial-review-demo',
  'game-show-round',
  'monte-carlo-demo',
  'clt-demo',
  'buffon-demo',
  'birthday-demo',
  'prisoners-demo',
  'patch-match-demo',
  'pixel-sweep-demo',
  'llm-watermark-demo',
  'ai-detector-demo',
  'bat-model-demo',
  'word-wall-demo',
  'whole-reply-demo',
  'coin-faker-demo',
  'ai-thinking-demo',
  'card-counting-demo',
  'fourier-demo',
  'chaos-pendulum-demo',
  'life-demo',
  'sync-demo',
  'times-table-demo',
  'eigen-demo',
  'soccer-demo',
  'belief-demo',
  'data-dump-demo',
  'sorting-line-demo',
  'logistic-machine-demo',
  'squash-stream-demo',
  'training-belt-demo',
  'model-quest-demo',
  'new-days-demo',
  'logistic-adventure-demo',
  'live-code-demo',
  'generative-model',
  'editable-demo',
  'packaged-demo',
  'agent-choice',
  'word-detective-demo',
  'student-picker-demo',
  'twist-picker-demo',
  'intro-card-demo',
  'twist-roulette-demo',
  'twist-chooser-demo',
  'pacing-dot',
] as const;

/** One of the registered {@link SLIDE_COMPONENT_IDS}. */
export type SlideComponentId = (typeof SLIDE_COMPONENT_IDS)[number];

/** Light/dark variant a {@link ComponentElementSchema} should render itself in. */
export const SlideComponentThemeSchema = z.enum(['light', 'dark']);

/** A light/dark theme choice for a {@link ComponentElementSchema}. See {@link SlideComponentThemeSchema}. */
export type SlideComponentTheme = z.infer<typeof SlideComponentThemeSchema>;

/**
 * When a demo's internal animation *begins*, in present mode. This is the
 * `idle → playing` edge of the three-phase lifecycle a trigger-aware demo
 * moves through (idle → playing → ended; see
 * `shared/componentRegistry.tsx`'s `SlideComponentProps.playPhase`):
 *
 * - `'on-enter'`: as soon as the slide is shown (the component mounts and
 *   runs immediately — this is the historical, default behavior). No idle
 *   phase.
 * - `'on-click'`: the demo shows its idle/first frame and waits; it starts
 *   playing when the presenter advances a build step. An on-click *start*
 *   therefore consumes one build step — by default `enterStep + 1` (see
 *   `shared/animationVisibility.ts`), but freely repositionable anywhere
 *   later in the build via {@link ComponentElementSchema.playStep}.
 *
 * Only meaningful for demos that opt in via `slideDemoRegistry`'s
 * `supportsPlayTrigger`; other components ignore it and always run on mount.
 * Edited via the "Play" ribbon control.
 */
export const SlideComponentPlayTriggerSchema = z.enum(['on-enter', 'on-click']);

/** When a demo's animation begins. See {@link SlideComponentPlayTriggerSchema}. */
export type SlideComponentPlayTrigger = z.infer<
  typeof SlideComponentPlayTriggerSchema
>;

/**
 * When a demo's internal animation *ends*, in present mode. This is the
 * `playing → ended` edge of the lifecycle (see
 * {@link SlideComponentPlayTriggerSchema}):
 *
 * - `'none'`: it never ends on its own build beat — a loop keeps looping and
 *   a one-shot rests on its final frame until the slide changes. Default.
 * - `'on-click'`: the presenter advances one more build step to freeze the
 *   demo on its final/idle frame. An on-click *end* consumes one build step —
 *   by default the one right after the play beat (see
 *   `shared/animationVisibility.ts`), but freely repositionable via
 *   {@link ComponentElementSchema.endStep}.
 *
 * Like the start trigger, only honored for `supportsPlayTrigger` demos and
 * edited via the "Play" ribbon control.
 */
export const SlideComponentEndTriggerSchema = z.enum(['none', 'on-click']);

/** When a demo's animation ends. See {@link SlideComponentEndTriggerSchema}. */
export type SlideComponentEndTrigger = z.infer<
  typeof SlideComponentEndTriggerSchema
>;

/**
 * A rendered picture of a component element's final frame, drawn in slide
 * thumbnails in place of the demo's generic preview. Written by the editor
 * (see `editor/DemoSnapshotter.tsx`); read through
 * `shared/demoSnapshot.ts`'s `currentDemoSnapshotSrc`, which ignores it once
 * `key` no longer matches the element.
 */
export const DemoSnapshotSchema = z.object({
  /** Download URL of the PNG in Storage. */
  src: z.string(),
  /**
   * Fingerprint of the element content the picture was taken of — see
   * `shared/demoSnapshot.ts`'s `demoSnapshotKey`.
   */
  key: z.string(),
  /**
   * The words the demo showed on the same frame, read from its DOM. Lets
   * readers that can't run the demo (simulated students, puzzle generation)
   * know what it says. Unset on snapshots taken before text was captured.
   */
  text: z.string().optional(),
  /**
   * A model's description of the picture and {@link text}: what the parts
   * show and how they relate. Unset until described, or when describing
   * failed.
   */
  description: z.string().optional(),
});

/** A component element's rendered picture. See {@link DemoSnapshotSchema}. */
export type DemoSnapshot = z.infer<typeof DemoSnapshotSchema>;

/**
 * An embedded interactive React component (e.g. a live demo), looked up by
 * `componentId` in `shared/componentRegistry.tsx`. Unlike the other element
 * types, its content isn't stored in the deck at all — the component
 * manages its own internal state.
 */
export const ComponentElementSchema = BaseElementSchema.extend({
  type: z.literal('component'),
  componentId: z.enum(SLIDE_COMPONENT_IDS),
  /** Falls back to `'light'` when unset — see `shared/ElementContent.tsx`. */
  theme: SlideComponentThemeSchema.optional(),
  /**
   * Optional presenter-set random seed, passed to the component so a run with
   * randomness can be made reproducible. Only meaningful for demos that opt in
   * via `slideDemoRegistry`'s `supportsSeed` (currently the dice demo); other
   * components ignore it. Unset means the component uses its own default
   * (non-reproducible) randomness. Edited via the "Seed" ribbon control.
   */
  seed: z.number().optional(),
  /**
   * Whether the demo draws a loading skeleton (a shimmer, then a fade-in)
   * while it boots. Omitted means `true`. `false` keeps the demo invisible
   * until its first render, then shows it at once with no placeholder or
   * fade. Only demos that opt in via `slideDemoRegistry`'s
   * `supportsLoadingSkeleton` read it; other components ignore it. Kept out
   * of `config` so toggling it never invalidates the element's
   * {@link ComponentElementSchema.snapshot}.
   */
  loadingSkeleton: z.boolean().optional(),
  /**
   * Opaque, demo-specific presenter configuration persisted with the slide
   * element. This schema deliberately does NOT know the config's shape — it
   * stores an arbitrary JSON object so that adding a *new* configurable demo
   * never has to touch this file (or the editor's `Toolbar`). Each demo
   * declares and validates its own config shape in its `slideDemoRegistry`
   * entry (webapp's `shared/componentRegistry.tsx`) and renders its own
   * `ribbonControls`; the parsed value is what the live component reads.
   * Unset means the demo falls back to its built-in defaults.
   */
  config: z.record(z.string(), z.unknown()).optional(),
  /**
   * When this demo's animation starts in present mode. See
   * {@link SlideComponentPlayTriggerSchema}. Unset is treated as `'on-enter'`
   * (play on arrival). Only demos with `supportsPlayTrigger` expose the
   * control; others ignore it entirely.
   */
  playTrigger: SlideComponentPlayTriggerSchema.optional(),
  /**
   * When this demo's animation stops (freezes) in present mode. See
   * {@link SlideComponentEndTriggerSchema}. Unset is treated as `'none'`
   * (never stops on its own beat). Only demos with `supportsPlayTrigger`
   * expose the control; others ignore it entirely.
   */
  endTrigger: SlideComponentEndTriggerSchema.optional(),
  /**
   * Explicit build step for this demo's play beat. See
   * {@link SlideComponentPlayTriggerSchema}. Only meaningful when
   * `playTrigger === 'on-click'`; ignored (and dropped at write time)
   * otherwise.
   *
   * Unset means the historical default: the step immediately after the
   * entrance step. When set, it must be strictly after the entrance step; a
   * stale value (e.g. after the entrance was dragged past it) falls back to
   * the default at read time — see `shared/animationVisibility.ts`'s
   * `getElementPlaySteps`, the only reader. Maintained by the animation
   * pane's drag-to-reorder (`shared/animationSequence.ts`), mirroring
   * {@link ComponentElementSchema.phaseSteps}.
   */
  playStep: z.number().int().nonnegative().optional(),
  /**
   * Explicit build step for this demo's stop beat. See
   * {@link SlideComponentEndTriggerSchema}. Only meaningful when
   * `endTrigger === 'on-click'`; ignored (and dropped at write time)
   * otherwise.
   *
   * Unset means the historical default: one step after the play beat. When
   * set, it must be strictly after the play beat; a stale value falls back
   * to the default at read time. Same maintenance story as
   * {@link ComponentElementSchema.playStep}.
   */
  endStep: z.number().int().nonnegative().optional(),
  /**
   * Explicit build steps for this demo's arrow-key phases, letting other
   * elements' entrances interleave with (or share a click with) the demo's
   * internal reveal. Entry `i` is the build step at which phase `i + 1`
   * begins — phase 0 always shows from the element's entrance step, so it has
   * no entry here. Only meaningful for demos with an `arrowKeyPhaseCount`
   * (see `shared/componentRegistry.tsx`); others ignore it.
   *
   * Unset means the historical default: phases advance on consecutive steps
   * starting at the element's entrance step. When set, steps must be strictly
   * increasing and after the entrance step; stale entries (e.g. after a config
   * change alters the phase count) are repaired at read time — see
   * `shared/animationVisibility.ts`'s `getComponentPhaseSteps`, the only
   * reader. Maintained by the animation pane's drag-to-reorder
   * (`shared/animationSequence.ts`).
   */
  phaseSteps: z.array(z.number().int().nonnegative()).optional(),
  /**
   * Picture of the demo's final frame for thumbnails. Unset until the editor
   * captures one; only demos rendered through the sandboxed iframe host get
   * one. See {@link DemoSnapshotSchema}.
   */
  snapshot: DemoSnapshotSchema.optional(),
});

/**
 * The vector-shape kinds a {@link ShapeElementSchema} can be. A rounded
 * rectangle is *not* a distinct kind — it is a `'rect'` with a nonzero
 * {@link ShapeElementSchema.cornerRadius}, so every rectangle can be rounded
 * (starting at radius 0). `'line'` is a straight segment between two opposite
 * corners of the element's box (see {@link ShapeElementSchema.lineDirection}).
 * `'freehand'` is a hand-drawn stroke: an open polyline through
 * {@link ShapeElementSchema.points} (e.g. a sketched arrow), stroked like a
 * line with no fill or text. `'dice'` is a six-sided die face (see
 * {@link ShapeElementSchema.diceValue}), rendered as a rounded tile with pips
 * — the same look as the standalone Dice Roll Demo
 * (`demos/dice-demo/components/DiceCanvas.tsx`). `'card'` is a playing card
 * (see {@link ShapeElementSchema.cardRank}, {@link ShapeElementSchema.cardSuit}
 * and {@link ShapeElementSchema.cardFaceDown}), drawn by
 * `shared/PlayingCard.tsx`.
 */
export const SHAPE_KINDS = [
  'rect',
  'circle',
  'line',
  'freehand',
  'dice',
  'card',
] as const;

/** One of the registered {@link SHAPE_KINDS}. */
export const ShapeKindSchema = z.enum(SHAPE_KINDS);

/** Playing-card ranks a `'card'` shape can show, ace low. */
export const CARD_RANKS = [
  'A',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  'J',
  'Q',
  'K',
] as const;

/** One of the {@link CARD_RANKS}. */
export const CardRankSchema = z.enum(CARD_RANKS);

/** A playing-card rank. See {@link CardRankSchema}. */
export type CardRank = z.infer<typeof CardRankSchema>;

/** Playing-card suits a `'card'` shape can show. */
export const CARD_SUITS = ['spades', 'hearts', 'diamonds', 'clubs'] as const;

/** One of the {@link CARD_SUITS}. */
export const CardSuitSchema = z.enum(CARD_SUITS);

/** A playing-card suit. See {@link CardSuitSchema}. */
export type CardSuit = z.infer<typeof CardSuitSchema>;

/** A vector-shape kind. See {@link ShapeKindSchema}. */
export type ShapeKind = z.infer<typeof ShapeKindSchema>;

/**
 * Which diagonal of the element's bounding box a `'line'` shape runs along:
 *
 * - `'downhill'`: top-left corner to bottom-right corner (`(0,0)`→`(w,h)`).
 * - `'uphill'`: bottom-left corner to top-right corner (`(0,h)`→`(w,0)`).
 *
 * A horizontal line is a `downhill`/`uphill` line in a box of near-zero height;
 * a vertical line, one of near-zero width. Resizing the box moves the endpoints
 * with its corners, so the same drag/resize handling as every other element
 * applies with no special-casing.
 */
export const SHAPE_LINE_DIRECTIONS = ['downhill', 'uphill'] as const;

/** The diagonal a line shape runs along. See {@link SHAPE_LINE_DIRECTIONS}. */
export const ShapeLineDirectionSchema = z.enum(SHAPE_LINE_DIRECTIONS);

/** A line shape's diagonal direction. See {@link ShapeLineDirectionSchema}. */
export type ShapeLineDirection = z.infer<typeof ShapeLineDirectionSchema>;

/**
 * A single point of a {@link ShapeElementSchema.points} freehand stroke,
 * stored **normalized** to the element's bounding box: `x`/`y` are fractions
 * in `[0, 1]` of the box's width/height. Storing fractions (rather than
 * canvas pixels) means the stroke scales with the box when it's resized, with
 * no separate original-size bookkeeping.
 */
export const ShapePointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

/** A normalized freehand-stroke point. See {@link ShapePointSchema}. */
export type ShapePoint = z.infer<typeof ShapePointSchema>;

/**
 * A vector shape, distinguished by {@link ShapeElementSchema.shapeKind}:
 * a rectangle (optionally rounded), a circle/ellipse, a straight line, or a
 * hand-drawn `freehand` stroke. Rects and circles render a fill + border and
 * hold an optional centered text box ({@link ShapeElementSchema.content});
 * lines and freehand strokes are stroke-only (no fill, no text). Styling
 * fields are all optional and fall back to the `DEFAULT_SHAPE_*` constants at
 * render time (see `shared/ElementContent.tsx`), so a shape can be stored
 * minimally and older shapes keep rendering as defaults change.
 */
export const ShapeElementSchema = BaseElementSchema.extend({
  type: z.literal('shape'),
  /** Which kind of shape this is. See {@link ShapeKindSchema}. */
  shapeKind: ShapeKindSchema,
  /**
   * CSS color string for the interior fill (rect/circle only). Omitted means
   * no fill (transparent) — the default for a freshly-drawn shape. Never set
   * for lines, which have no interior.
   */
  fillColor: z.string().optional(),
  /**
   * CSS color string for the border (rect/circle) or stroke (line). Falls back
   * to the deck master's default element color
   * ({@link SlideMasterSchema.elementColor}) when unset.
   */
  borderColor: z.string().optional(),
  /**
   * Border/stroke width in canvas-space pixels. Falls back to
   * {@link DEFAULT_SHAPE_BORDER_WIDTH} when unset. Zero is allowed (no border).
   */
  borderWidth: z.number().nonnegative().optional(),
  /**
   * Corner radius in canvas-space pixels (`'rect'` only; ignored for other
   * kinds). Unset or `0` means square corners — a plain rectangle. A larger
   * value rounds the corners; the renderer clamps it to half the shorter side.
   */
  cornerRadius: z.number().nonnegative().optional(),
  /** Which diagonal a `'line'` runs along (line only). See {@link ShapeLineDirectionSchema}. */
  lineDirection: ShapeLineDirectionSchema.optional(),
  /**
   * The hand-drawn stroke path (`'freehand'` only), as an ordered list of
   * box-normalized {@link ShapePointSchema} points rendered as an open
   * polyline. Never set for the other kinds.
   */
  points: z.array(ShapePointSchema).optional(),
  /**
   * Face shown, 1-6 (`'dice'` only; ignored for other kinds). Falls back to
   * {@link DEFAULT_DICE_VALUE} when unset. The die body's color reuses
   * {@link ShapeElementSchema.fillColor} (falling back to
   * {@link DEFAULT_DICE_FILL_COLOR}); the pip color is derived automatically
   * for contrast (see `shared/diceShape.ts`'s `dicePipColor`), so it isn't a
   * stored field.
   */
  diceValue: z.number().int().min(1).max(6).optional(),
  /**
   * Rank shown (`'card'` only; ignored for other kinds). Falls back to
   * {@link DEFAULT_CARD_RANK} when unset.
   */
  cardRank: CardRankSchema.optional(),
  /**
   * Suit shown (`'card'` only; ignored for other kinds). Falls back to
   * {@link DEFAULT_CARD_SUIT} when unset. Hearts and diamonds draw in the red
   * ink, spades and clubs in the dark ink (see `shared/cardShape.ts`).
   */
  cardSuit: CardSuitSchema.optional(),
  /**
   * Whether the card shows its back instead of its face (`'card'` only).
   * Unset means face up. The back's color reuses
   * {@link ShapeElementSchema.fillColor}, falling back to
   * {@link DEFAULT_CARD_BACK_COLOR}; the face is always card white.
   */
  cardFaceDown: z.boolean().optional(),
  /**
   * Centered rich-text content (rect/circle only; same shape as
   * {@link TextElementSchema.content}). Omitted means the shape has no text.
   * Never set for lines or freehand strokes.
   */
  content: RichTextContentSchema.optional(),
  /**
   * Font size in slide points (pt) for {@link ShapeElementSchema.content},
   * matching PowerPoint font sizes — NOT canvas pixels; rendered at 2 canvas
   * px per pt (see `utils/slideUnits.ts`). Falls back to
   * {@link DEFAULT_SHAPE_FONT_SIZE} when unset. Only meaningful for
   * rect/circle shapes that carry text.
   */
  fontSize: z.number().positive().optional(),
  /**
   * CSS color string for the shape's text (rect/circle). Falls back to the
   * deck master's default element color ({@link SlideMasterSchema.elementColor})
   * when unset. Named `color` (not `textColor`) to match
   * {@link TextElementSchema.color}/{@link EquationElementSchema.color}, so the
   * ribbon's shared font-size/color controls act on a shape's text with no
   * special-casing.
   */
  color: z.string().optional(),
});

/**
 * The kinds of {@link PlaceholderElementSchema} that exist. A placeholder marks
 * a reserved region on a slide master (see {@link SlideMasterSchema}) rather
 * than carrying content of its own:
 *
 * - `'camera'`: the picture-in-picture region reserved for the presenter's
 *   camera/screen feed. Rendered as an outlined box (see
 *   `shared/ElementContent.tsx`), never a real video — the actual feed is
 *   composited externally (OBS/Zoom) over this region.
 * - `'title'`: the region + typography a slide's title text box should match.
 *   Rendered as a dashed guide box (see `shared/ElementContent.tsx`) while
 *   editing the master; a slide's own text box copies this placeholder's
 *   position/size/`fontSize`/`color`/`fontFamily` as a one-time snapshot via
 *   the "Make Title" right-click command (`data/useDeckEditor.ts`'s
 *   `makeElementTitle`), not a live link — matching how master → slide styling
 *   already works everywhere else (e.g. {@link TextElementSchema.color}'s
 *   fallback to {@link SlideMasterSchema.elementColor}).
 *
 * New kinds (slide number, date, footer, ...) can be added here without a
 * schema migration; unknown stored kinds coerce to `'camera'` (see
 * {@link PlaceholderElementSchema}).
 */
export const PLACEHOLDER_KINDS = ['camera', 'title'] as const;

/** One of the registered {@link PLACEHOLDER_KINDS}. */
export const PlaceholderKindSchema = z.enum(PLACEHOLDER_KINDS);

/** A placeholder kind. See {@link PlaceholderKindSchema}. */
export type PlaceholderKind = z.infer<typeof PlaceholderKindSchema>;

/**
 * A placeholder element: a positionable, non-content region (the camera box,
 * or the title guide) that typically lives on a slide master
 * ({@link SlideMasterSchema}). It reuses {@link BaseElementSchema}'s
 * position/size so it drags, resizes, and renders through the exact same
 * element machinery (`ElementContent`, `ElementBox`) as any other element.
 */
export const PlaceholderElementSchema = BaseElementSchema.extend({
  type: z.literal('placeholder'),
  /**
   * Which placeholder this is. A stored value we no longer recognize is coerced
   * to `'camera'` here via `.catch`, so old decks keep loading after a kind is
   * renamed or removed (mirrors {@link TableElementSchema.borderStyle}).
   */
  placeholderKind: PlaceholderKindSchema.catch('camera'),
  /**
   * Font size (slide points, matching {@link TextElementSchema.fontSize}) a
   * slide's title text box should copy from this placeholder. Only meaningful
   * for `placeholderKind: 'title'`; ignored for `'camera'`. Falls back to
   * {@link DEFAULT_TITLE_FONT_SIZE} when unset. Named `fontSize` (not
   * `titleFontSize`) to match {@link TextElementSchema.fontSize}, so the
   * ribbon's shared font-size/color controls (`editor/Toolbar.tsx`) act on the
   * title placeholder with no special-casing — the same reasoning as
   * {@link ShapeElementSchema.color}'s doc comment.
   */
  fontSize: z.number().positive().optional(),
  /**
   * CSS color string a slide's title text box should copy from this
   * placeholder. Only meaningful for `placeholderKind: 'title'`; ignored for
   * `'camera'`. Falls back to the deck master's
   * {@link SlideMasterSchema.elementColor} when unset (same convention, and
   * same field name, as {@link TextElementSchema.color}).
   */
  color: z.string().optional(),
  /**
   * CSS `font-family` value a slide's title text box should copy from this
   * placeholder — the same value space as a text run's `fontFamily` mark (see
   * `shared/textExtensions.ts`). Only meaningful for `placeholderKind: 'title'`;
   * ignored for `'camera'`. Falls back to the deck-wide default font
   * (`shared/slideTheme.ts`'s `SLIDE_FONT_FAMILY`) when unset — the same
   * fallback a text run gets when it carries no `fontFamily` mark.
   */
  fontFamily: z.string().optional(),
});

/**
 * A single element on a slide (or slide master): a {@link TextElementSchema},
 * an {@link ImageElementSchema}, a {@link VideoElementSchema}, an
 * {@link EquationElementSchema}, a {@link TableElementSchema}, a
 * {@link ComponentElementSchema}, a {@link ShapeElementSchema}, or a
 * {@link PlaceholderElementSchema}, distinguished by `type`.
 */
export const ElementSchema = z.discriminatedUnion('type', [
  TextElementSchema,
  ImageElementSchema,
  VideoElementSchema,
  EquationElementSchema,
  MorphingTextElementSchema,
  TableElementSchema,
  ComponentElementSchema,
  ShapeElementSchema,
  PlaceholderElementSchema,
]);

/**
 * A per-slide background audio track (e.g. music) that plays during
 * presentation. The audio object is uploaded to the same Storage assets path
 * as images and videos (see `data/slideAssetCache.ts`'s `uploadSlideAsset`), so
 * it inherits the identical immutable-caching contract. This is a slide
 * property, not a positioned element: it has no canvas position or size, and
 * exactly one track (or none) per slide.
 *
 * In present mode, entering the slide autoplays the track from the start and
 * leaving it stops the track (pause + reset) — it never carries across slides
 * (see `present/SlidesPresentPage.tsx`). The editor canvas stays silent while
 * authoring; the ribbon's music control offers an out-of-band preview instead
 * (see `editor/SlideAudioControl.tsx`).
 */
export const SlideAudioSchema = z.object({
  /**
   * Firebase Storage download URL for the audio object under
   * `slides/{courseId}/{slideProjectId}/assets/`, uploaded via
   * `uploadSlideAsset` (the same helper images/videos use).
   */
  src: z.string(),
  /**
   * Whether the track repeats for as long as the slide is shown. Absent (or
   * `false`) means it plays through once and stops. The editor drops the key
   * rather than storing `false` (Firestore rejects `undefined`, and an absent
   * key already means "no loop"), matching {@link SlideSchema.hidden}'s
   * optional-boolean convention.
   */
  loop: z.boolean().optional(),
});

/** A per-slide audio track. See {@link SlideAudioSchema}. */
export type SlideAudio = z.infer<typeof SlideAudioSchema>;

/**
 * One slide in a deck. `elements` order is significant: it is the z-order,
 * with later elements rendered on top of earlier ones. There is no separate
 * z-index field in this MVP.
 */
export const SlideSchema = z.object({
  /** Stable identifier, unique within the deck. */
  id: z.string(),
  elements: z.array(ElementSchema),
  /**
   * Free-form plain-text speaker notes for the slide (e.g. talking points).
   * Never rendered in the editor canvas or present mode, but shown alongside
   * the slide in the read-only view page's notes pane
   * (`slides/view/SlidesViewPage.tsx`) and in the embedded preview's
   * "Speaker notes" callout (`slides/embed/SlideDeckPreview.tsx`) — so they
   * are visible to anyone who can view the deck, not a private authoring
   * aid. Undefined means no notes have been written yet.
   */
  notes: z.string().optional(),
  /**
   * When true, this slide does not render the deck's slide master
   * ({@link SlideMasterSchema}) elements beneath its own content — e.g. a
   * full-bleed image slide that shouldn't show the logo or camera box. The
   * master's background color still applies. Undefined/false means "show the
   * master." This is the successor to {@link hideDefaultLogo}.
   */
  hideMaster: z.boolean().optional(),
  /**
   * Per-slide background color (any CSS color string) overriding the deck
   * master's {@link SlideMasterSchema.backgroundColor} for this slide only.
   * Undefined means "use the master's background," so slides saved before this
   * field existed are unchanged. Only the background is affected — the master's
   * foreground elements still follow {@link hideMaster}.
   */
  backgroundColor: z.string().optional(),
  /**
   * @deprecated Legacy per-slide opt-out of the default Stanford logo, from
   * before the slide-master model. Still parsed so decks saved with it keep
   * loading, but no longer written: {@link migrateDeckMaster} converts a
   * `true` value to {@link hideMaster} on read. Do not set this in new code.
   */
  hideDefaultLogo: z.boolean().optional(),
  /**
   * When true, this slide is skipped in present mode entirely — it never
   * appears and navigation steps straight over it — while still showing in
   * the editor's filmstrip (dimmed, with a "Hidden" marker) so the author can
   * unhide it. Toggled from the slide's right-click menu. Undefined/false
   * means "show the slide." Mirrors {@link hideMaster}'s optional-boolean
   * convention: an absent key means visible, so the toggle drops the key
   * rather than writing `false` (Firestore's `setDoc` rejects `undefined`).
   */
  hidden: z.boolean().optional(),
  /**
   * Per-build-step start behavior in present mode (see
   * {@link StepStartTriggerSchema}), keyed by the step number as a string
   * (e.g. `"2"`). A step absent from this map — and step 0, always — defaults
   * to `'on-click'`. Keyed by a string rather than stored as a positional
   * array because Firestore's `setDoc` rejects arrays nested inside another
   * array element, and every slide already lives in the deck's `slides` array
   * (the same constraint that shaped {@link MorphPlanSchema.matchedIndexes}).
   * Only non-default overrides are stored; a step left on `'on-click'` is
   * simply omitted, and an empty map is dropped entirely (see
   * `data/useDeckEditor.ts`'s `updateSlideStepTrigger`).
   */
  stepTriggers: z.record(z.string(), StepStartTriggerSchema).optional(),
  /**
   * Per-build-step teleprompter script, keyed by the step number as a string
   * (e.g. `"0"`) — string-keyed for the same Firestore nested-array reason as
   * {@link stepTriggers}. Each entry is the text a presenter reads aloud from
   * that step onward. Sparse by design: a step with no entry *continues* the
   * previous text — the prompter view (`slides/prompter/TeleprompterView.tsx`)
   * shows the entry at the greatest scripted step ≤ the current step (see
   * `shared/stepScripts.ts`'s `getActiveStepScript`), so a single `"0"` entry
   * scripts the whole slide and an entry at `"5"` swaps the text only when
   * the build reaches step 5. Unlike {@link stepTriggers}, step `"0"` (the
   * slide's initial state) is a valid key. Authored in the editor's bottom
   * Notes | Script dock; only non-empty entries are stored and an empty map
   * is dropped entirely (see `data/useDeckEditor.ts`'s
   * `updateSlideStepScript`). Reordering or repacking a slide's build carries
   * each entry with its step group (see `shared/animationSequence.ts`).
   * Lives in the deck document, so it shares {@link notes}' course-wide read
   * visibility — not a private authoring aid.
   */
  stepScripts: z.record(z.string(), z.string()).optional(),
  /**
   * Optional background audio track for this slide (see
   * {@link SlideAudioSchema}). In present mode, entering the slide autoplays it
   * from the start and leaving stops it — it does not persist across slides; the
   * editor canvas stays silent. A new optional field, so decks saved before it
   * keep loading (the "migrate on read" convention); absent means the slide has
   * no audio, i.e. the existing behavior.
   */
  audio: SlideAudioSchema.optional(),
  /**
   * When true, present mode does not draw the pacing dot on this slide (see
   * `pacing/pacingPlan.ts` and {@link DeckPacingSchema}). Absent means "show
   * the dot" whenever the deck has a pacing plan; the toggle drops the key
   * rather than writing `false`, like {@link hidden}. The slide still gets a
   * planned time window — only the dot is hidden.
   */
  hidePacingDot: z.boolean().optional(),
});

/**
 * Default color a deck's master uses for elements that don't set their own —
 * white, matching the historic hardcoded text/shape-outline color that read
 * against the {@link DEFAULT_MASTER_BACKGROUND_COLOR} dark background. Kept as
 * a literal here (rather than importing `shared/slideTheme.ts`) so `deck.ts`
 * stays framework/render-agnostic. Declared before {@link SlideMasterSchema}
 * because the schema references it at evaluation time (`.default(...)`).
 */
export const DEFAULT_MASTER_ELEMENT_COLOR = '#ffffff';

/**
 * A slide master: a shared layer rendered *beneath* every slide's own content
 * (unless the slide sets {@link SlideSchema.hideMaster}). It owns the deck's
 * background color and a set of {@link ElementSchema} elements positioned once
 * and shown on every slide — the Stanford logo, the camera placeholder, and
 * anything else the author adds. Master elements reuse the full element union,
 * so any element type works on the master; their `animation` (build steps) is
 * ignored, since the master is static. There is exactly one master per deck in
 * this model (see {@link DeckSchema.master}).
 */
export const SlideMasterSchema = z.object({
  /** CSS color string painted as the slide-canvas background (e.g. `'#000000'`). */
  backgroundColor: z.string(),
  /**
   * CSS color string used as the deck-wide default for slide elements that
   * don't set their own color — text ({@link TextElementSchema.color}), shape
   * outlines ({@link ShapeElementSchema.borderColor}), and table outlines. An
   * element with an explicit color always wins; this is only the fallback. New
   * to the schema, so a `.default` of {@link DEFAULT_MASTER_ELEMENT_COLOR}
   * fills it in when a deck saved before this field existed is parsed on read
   * (the same "migrate on read" convention as {@link DeckSchema.master}), which
   * means the output type is always a `string`.
   */
  elementColor: z.string().default(DEFAULT_MASTER_ELEMENT_COLOR),
  /**
   * The master's elements, in z-order (later on top), rendered beneath each
   * slide's own elements. Element ids must stay unique within the whole deck,
   * disjoint from slide element ids — the auto-created ones use a `master-`
   * prefix and author-added ones use `crypto.randomUUID()`.
   */
  elements: z.array(ElementSchema),
});

/**
 * A deck's section pacing plan: how long the section runs and which slides the
 * presenter should have reached by which minute. Present mode turns this into
 * one planned time window per shown slide (`pacing/pacingPlan.ts`'s
 * `resolvePlannedWindows`) and compares the section clock against it to color
 * the pacing dot. Authored in the editor's Pacing tab
 * (`editor/NotesScriptDock.tsx`).
 */
export const DeckPacingSchema = z.object({
  /**
   * Planned section length in minutes. The last shown slide's planned window
   * closes here.
   */
  sectionMinutes: z.number().positive(),
  /**
   * "Reach this slide by minute N" checkpoints keyed by slide id — string
   * keys for the same Firestore nested-array reason as
   * {@link SlideSchema.stepTriggers}. Slides without an entry are spread
   * evenly between the nearest checkpoints on either side. The first shown
   * slide always starts at minute 0, so an entry for it is ignored, as are
   * entries for ids no longer in the deck and entries on hidden slides.
   */
  checkpoints: z.record(z.string(), z.number().nonnegative()),
});

/**
 * A full slide deck ("slide project"), as persisted to the Firestore document
 * at `slides/{courseId}/decks/{slideProjectId}`, or, for a section leader's
 * personal copy, under `slDeckCopies` (see `data/slideDeckPaths.ts`).
 */
export const DeckSchema = z.object({
  title: z.string(),
  slides: z.array(SlideSchema).min(1),
  /**
   * The deck's section pacing plan (see {@link DeckPacingSchema}). Absent
   * means the deck is not paced: present mode draws no pacing dot at all.
   * Turned on and off from the editor's Pacing tab, which drops the key
   * rather than writing an empty plan.
   */
  pacing: DeckPacingSchema.optional(),
  /**
   * The deck's slide master (see {@link SlideMasterSchema}). Optional on the
   * persisted shape only so decks saved before the master existed still parse;
   * {@link migrateDeckMaster}, applied at every read, guarantees it is present
   * in memory (and therefore on every subsequent save).
   */
  master: SlideMasterSchema.optional(),
  /**
   * This deck's position in the pre-folder flat deck list, lower sorting
   * first, written by that list's drag-reorder. No longer written: order
   * now lives in the slides library (`home/libraryModel.ts`), which reads
   * this as the fallback for a deck without a placement, so the root keeps
   * its hand-made order. The deck editor's autosave round-trips it
   * unchanged since it's part of this schema.
   */
  order: z.number().optional(),
  /**
   * The uid of the section leader who owns this deck. Set only on a
   * personal copy of a section deck (see `section/slDeckCopy.ts`); absent on
   * every staff deck. Firestore rules keep a copy private to this user and
   * staff, and refuse a save that changes it.
   */
  ownerUid: z.string().optional(),
  /**
   * The id of the official deck this personal copy was made from. Set only
   * alongside {@link ownerUid}.
   */
  sourceDeckId: z.string().optional(),
  /**
   * The source deck's content fingerprint when this copy was made (see
   * `section/slDeckCopy.ts`'s `deckContentFingerprint`). A different
   * fingerprint on the source now means staff changed it after the copy was
   * made. Set only alongside {@link ownerUid}.
   */
  sourceFingerprint: z.string().optional(),
});

/** A 2D point in canvas space. See {@link PositionSchema}. */
export type Position = z.infer<typeof PositionSchema>;
/** A width/height pair in canvas-space pixels. See {@link SizeSchema}. */
export type Size = z.infer<typeof SizeSchema>;
/** Rich text content for a text element. See {@link RichTextContentSchema}. */
export type RichTextContent = z.infer<typeof RichTextContentSchema>;
/** A text element. See {@link TextElementSchema}. */
export type TextElement = z.infer<typeof TextElementSchema>;
/** A non-destructive image crop rectangle. See {@link ImageCropSchema}. */
export type ImageCrop = z.infer<typeof ImageCropSchema>;
/** An image element. See {@link ImageElementSchema}. */
export type ImageElement = z.infer<typeof ImageElementSchema>;
/** A video element. See {@link VideoElementSchema}. */
export type VideoElement = z.infer<typeof VideoElementSchema>;
/** A block equation element. See {@link EquationElementSchema}. */
export type EquationElement = z.infer<typeof EquationElementSchema>;
/** One state (keyframe) of a morphing-text element. See {@link MorphTextStateSchema}. */
export type MorphTextState = z.infer<typeof MorphTextStateSchema>;
/** A morphing-text element: an ordered list of LaTeX states. See {@link MorphingTextElementSchema}. */
export type MorphingTextElement = z.infer<typeof MorphingTextElementSchema>;
/** A table element. See {@link TableElementSchema}. */
export type TableElement = z.infer<typeof TableElementSchema>;
/** An embedded interactive component element. See {@link ComponentElementSchema}. */
export type ComponentElement = z.infer<typeof ComponentElementSchema>;
/** A vector shape element (rect, circle, line, or freehand stroke). See {@link ShapeElementSchema}. */
export type ShapeElement = z.infer<typeof ShapeElementSchema>;
/** A placeholder element (e.g. the camera box). See {@link PlaceholderElementSchema}. */
export type PlaceholderElement = z.infer<typeof PlaceholderElementSchema>;
/** A slide element (text, image, video, equation, table, component, shape, or placeholder). See {@link ElementSchema}. */
export type Element = z.infer<typeof ElementSchema>;
/** A single slide. See {@link SlideSchema}. */
export type Slide = z.infer<typeof SlideSchema>;
/** A deck's slide master. See {@link SlideMasterSchema}. */
export type SlideMaster = z.infer<typeof SlideMasterSchema>;
/** A deck's section pacing plan. See {@link DeckPacingSchema}. */
export type DeckPacing = z.infer<typeof DeckPacingSchema>;
/** A full slide deck. See {@link DeckSchema}. */
export type Deck = z.infer<typeof DeckSchema>;

/** Default slide/element factory values, shared by the editor's "add" actions. */
export const DEFAULT_TEXT_SIZE: Size = {width: 480, height: 120};
export const DEFAULT_TEXT_POSITION: Position = {x: 100, y: 100};
export const DEFAULT_FONT_SIZE = 24;
export const DEFAULT_TEXT_CONTENT: RichTextContent = {
  json: {
    type: 'doc',
    content: [{type: 'paragraph', content: [{type: 'text', text: 'New text'}]}],
  },
  plainText: 'New text',
};
export const MAX_IMAGE_DIMENSION = 800;
/**
 * Cap, in canvas-space pixels, on a freshly-inserted video element's longer
 * side — the video analogue of {@link MAX_IMAGE_DIMENSION}, a little larger
 * since clips are usually the slide's centerpiece. The natural aspect ratio
 * is preserved; the user can resize afterwards.
 */
export const MAX_VIDEO_DIMENSION = 960;
/**
 * Bounding box a freshly-inserted video falls back to when its metadata
 * (natural dimensions) can't be read — a 16:9 box, the overwhelmingly common
 * video aspect ratio.
 */
export const FALLBACK_VIDEO_SIZE: Size = {width: 960, height: 540};
/**
 * Default bounding box/font size for a newly-inserted title text element —
 * wide and short, docked near the top of the {@link CANVAS_WIDTH}x
 * {@link CANVAS_HEIGHT} canvas, at a much larger font size than
 * {@link DEFAULT_FONT_SIZE} — mirroring a slide's title placeholder in
 * PowerPoint/Google Slides. See the "Add Text" ribbon dropdown in
 * `editor/AddTextMenu.tsx`.
 */
export const DEFAULT_TITLE_POSITION: Position = {x: 100, y: 60};
export const DEFAULT_TITLE_SIZE: Size = {width: 1720, height: 120};
export const DEFAULT_TITLE_FONT_SIZE = 64;
export const DEFAULT_TITLE_CONTENT: RichTextContent = {
  json: {
    type: 'doc',
    content: [{type: 'paragraph', content: [{type: 'text', text: 'Title'}]}],
  },
  plainText: 'Title',
};
export const DEFAULT_EQUATION_SIZE: Size = {width: 480, height: 160};
export const DEFAULT_EQUATION_POSITION: Position = {x: 100, y: 100};
export const DEFAULT_EQUATION_LATEX = 'y = mx + b';
export const DEFAULT_MORPHING_TEXT_SIZE: Size = {width: 640, height: 200};
export const DEFAULT_MORPHING_TEXT_POSITION: Position = {x: 100, y: 100};
/** Default font size (slide points — see `utils/slideUnits.ts`) for a newly-inserted morphing-text element; adjust per element from the ribbon's font-size control. */
export const DEFAULT_MORPHING_TEXT_FONT_SIZE = 24;
/**
 * Seed states for a freshly-inserted {@link MorphingTextElementSchema} — two
 * keyframes chosen to visibly demonstrate the morph: the shared `a^2 + b^2`
 * glyphs glide while `= c^2` fades in on the second click.
 */
export const DEFAULT_MORPHING_TEXT_STATES: MorphTextState[] = [
  {latex: 'a^2 + b^2'},
  {latex: 'a^2 + b^2 = c^2'},
];
export const DEFAULT_TABLE_POSITION: Position = {x: 100, y: 100};
export const DEFAULT_TABLE_FONT_SIZE = 28;
/**
 * Default bounding box for a newly-inserted component element. Sized much
 * larger than the other defaults since these are full mini-apps (e.g.
 * `llm-demo`), not a text box.
 */
export const DEFAULT_COMPONENT_SIZE: Size = {width: 1200, height: 800};
export const DEFAULT_COMPONENT_POSITION: Position = {x: 100, y: 100};

/**
 * Default bounding-box size for a shape created by a plain click (no drag) in
 * the draw-to-create flow. A drawn shape uses the box the user dragged out
 * instead; this is only the click-without-dragging fallback (see
 * `editor/Canvas.tsx`). Circles start square so the fallback is a circle, not
 * an ellipse; rects start wider than tall.
 */
export const DEFAULT_SHAPE_SIZE: Record<ShapeKind, Size> = {
  rect: {width: 800, height: 240},
  circle: {width: 240, height: 240},
  // A visible diagonal, so a click-without-drag still yields a grabbable line
  // (a drawn line uses the dragged box instead; a near-horizontal/vertical
  // drawn line gets a thin — but positive-sized — box, see `editor/Canvas.tsx`).
  line: {width: 800, height: 200},
  // Never actually used: a freehand stroke requires a drag to trace a path, so
  // a click-without-drag creates nothing (see `editor/Canvas.tsx`). Present
  // only to satisfy the exhaustive `Record<ShapeKind, Size>`.
  freehand: {width: 1, height: 1},
  // Square, like `circle` — a die is never elongated.
  dice: {width: 200, height: 200},
  // Keeps the playing-card aspect of `CARD_ASPECT` (see `shared/cardShape.ts`).
  card: {width: 200, height: 280},
};
/** Border/stroke width, in canvas-space pixels, a freshly-created shape starts with. */
export const DEFAULT_SHAPE_BORDER_WIDTH = 3;
/**
 * Stroke width, in canvas-space pixels, a freshly-drawn `'freehand'` pen stroke
 * starts with. Wider than {@link DEFAULT_SHAPE_BORDER_WIDTH} because hand-drawn
 * ink reads better with a heavier line than a geometric shape's border.
 */
export const DEFAULT_FREEHAND_BORDER_WIDTH = 6;
/**
 * Corner radius, in canvas-space pixels, seeded when the user picks "Rounded
 * rectangle" from the shapes dropdown. A plain "Rectangle" seeds `0`.
 */
export const DEFAULT_ROUNDED_RECT_RADIUS = 24;
/** Font size, in canvas-space pixels, for a shape's centered text box. */
export const DEFAULT_SHAPE_FONT_SIZE = 32;
/**
 * Empty rich-text content for a new rect/circle shape's centered text box. The
 * paragraph is seeded `textAlign: 'center'` (see `shared/textExtensions.ts`'s
 * `TextAlign`) so a shape's text is horizontally centered by default — matching
 * its vertical centering — and stays consistent between the read-only and
 * inline-editing renders. The user can re-align it from the ribbon afterward.
 */
export const DEFAULT_SHAPE_TEXT_CONTENT: RichTextContent = {
  json: {
    type: 'doc',
    content: [{type: 'paragraph', attrs: {textAlign: 'center'}}],
  },
  plainText: '',
};
/** Face value ({@link ShapeElementSchema.diceValue}) a freshly-created `'dice'` shape starts showing. */
export const DEFAULT_DICE_VALUE = 1;
/** Rank ({@link ShapeElementSchema.cardRank}) a freshly-created `'card'` shape starts showing. */
export const DEFAULT_CARD_RANK: CardRank = 'A';
/** Suit ({@link ShapeElementSchema.cardSuit}) a freshly-created `'card'` shape starts showing. */
export const DEFAULT_CARD_SUIT: CardSuit = 'spades';
/** Back color ({@link ShapeElementSchema.fillColor}) a `'card'` shape uses when face down and no color is set. */
export const DEFAULT_CARD_BACK_COLOR = '#339af0';
/**
 * Die body color ({@link ShapeElementSchema.fillColor}) a freshly-created
 * `'dice'` shape starts with — the plain (near-black) face from the
 * standalone Dice Roll Demo's `demos/dice-demo/components/DiceCanvas.tsx`.
 */
export const DEFAULT_DICE_FILL_COLOR = '#2a2e37';
/** Canvas-space pixel width/height a newly-inserted table cell starts at, before the user resizes the element box. */
const DEFAULT_TABLE_CELL_SIZE = {width: 160, height: 60};

/**
 * Canvas-space size for a brand-new table element, scaled to its
 * `rows`/`columns` count via {@link DEFAULT_TABLE_CELL_SIZE} so a freshly
 * inserted 5x5 table isn't the same tiny box as a freshly inserted 2x2 one.
 *
 * @param args.rows - Number of rows the table will have.
 * @param args.columns - Number of columns the table will have.
 * @return The default bounding box size for the new table element.
 */
export function defaultTableSize({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}): Size {
  return {
    width: columns * DEFAULT_TABLE_CELL_SIZE.width,
    height: rows * DEFAULT_TABLE_CELL_SIZE.height,
  };
}

/**
 * Builds the initial TipTap/ProseMirror JSON document for a brand-new table
 * element: a single `table` node with `rows` x `columns` cells, each holding
 * one empty paragraph. Matches the node shape produced by the TipTap table
 * extensions in `shared/tableExtensions.ts` (`table` > `tableRow` >
 * `tableCell` > `paragraph`).
 *
 * @param args.rows - Number of rows to create. Must be at least 1.
 * @param args.columns - Number of columns to create. Must be at least 1.
 * @return A {@link RichTextContent} value ready to store on a new
 *     {@link TableElement}.
 */
export function createTableContent({
  rows,
  columns,
}: {
  rows: number;
  columns: number;
}): RichTextContent {
  const cell = {
    type: 'tableCell',
    content: [{type: 'paragraph'}],
  };
  const row = {
    type: 'tableRow',
    content: Array.from({length: columns}, () => cell),
  };
  return {
    json: {
      type: 'doc',
      content: [
        {
          type: 'table',
          content: Array.from({length: rows}, () => row),
        },
      ],
    },
    plainText: '',
  };
}

/**
 * Static path (served from the webapp's `public/` directory, not Firebase
 * Storage) of the cardinal-red Stanford seal used as every slide's default
 * logo. Transparent outside the circle, so it sits cleanly on the slide
 * canvas's black background. See {@link createDefaultLogoElement}.
 */
export const DEFAULT_LOGO_SRC = STANFORD_LOGO_SRC;
/**
 * Canvas-space size for the default logo — square, matching the seal
 * image's native 1:1 aspect ratio (370x370px).
 */
export const DEFAULT_LOGO_SIZE: Size = {width: 140, height: 140};
/** Canvas-space gap from the bottom/right canvas edges for the default logo. */
const DEFAULT_LOGO_MARGIN = 40;
/** Canvas-space position docking the default logo to the bottom-right corner. */
export const DEFAULT_LOGO_POSITION: Position = {
  x: CANVAS_WIDTH - DEFAULT_LOGO_SIZE.width - DEFAULT_LOGO_MARGIN,
  y: CANVAS_HEIGHT - DEFAULT_LOGO_SIZE.height - DEFAULT_LOGO_MARGIN,
};

/**
 * Deterministic element id of the *legacy* per-slide default logo, from before
 * the slide-master model, when the logo was synthesized onto each slide (and,
 * once repositioned, persisted into that slide's `elements`). Derived from the
 * slide id so it was unique within the deck. The logo now lives once on the
 * master ({@link createMasterLogoElement}); this id survives only so
 * {@link migrateDeckMaster} can find and strip those stale per-slide copies.
 *
 * @param slideId - Id of the slide the legacy logo belonged to.
 * @return The legacy logo element's id for that slide.
 */
export function defaultLogoElementId(slideId: string): string {
  return `default-logo-${slideId}`;
}

/**
 * Background color a deck's master starts with — black, matching the historic
 * hardcoded slide background. Kept as a literal here (rather than importing
 * `shared/slideTheme.ts`) so `deck.ts` stays framework/render-agnostic.
 */
export const DEFAULT_MASTER_BACKGROUND_COLOR = '#000000';

/** Element id of the master's Stanford logo. See {@link createMasterLogoElement}. */
export const MASTER_LOGO_ELEMENT_ID = 'master-logo';
/** Element id of the master's camera placeholder. See {@link createCameraPlaceholderElement}. */
export const MASTER_CAMERA_ELEMENT_ID = 'master-camera';

/**
 * Canvas-space size of the camera placeholder — 480x270, i.e. 25% of the
 * {@link CANVAS_WIDTH}x{@link CANVAS_HEIGHT} canvas at a 16:9 aspect ratio.
 */
export const CAMERA_PLACEHOLDER_SIZE: Size = {width: 480, height: 270};
/** Canvas-space gap from the top/right canvas edges for the default camera placeholder. */
const CAMERA_PLACEHOLDER_MARGIN = 20;
/** Canvas-space position docking the default camera placeholder to the top-right corner. */
export const CAMERA_PLACEHOLDER_POSITION: Position = {
  x: CANVAS_WIDTH - CAMERA_PLACEHOLDER_SIZE.width - CAMERA_PLACEHOLDER_MARGIN,
  y: CAMERA_PLACEHOLDER_MARGIN,
};

/** Element id of the master's title placeholder. See {@link createTitlePlaceholderElement}. */
export const MASTER_TITLE_PLACEHOLDER_ELEMENT_ID = 'master-title';

/**
 * Canvas-space pixels per inch, used only to compute
 * {@link DEFAULT_TITLE_PLACEHOLDER_POSITION} — see {@link CANVAS_PX_PER_INCH}.
 */
const TITLE_PLACEHOLDER_LEFT_OFFSET_INCHES = 6;

/**
 * Default canvas-space position for the title placeholder: flush with the
 * canvas top edge, with its left edge
 * {@link TITLE_PLACEHOLDER_LEFT_OFFSET_INCHES} inches left of horizontal
 * center. Purely a starting point — an author can drag it anywhere in
 * master-edit mode.
 */
export const DEFAULT_TITLE_PLACEHOLDER_POSITION: Position = {
  x:
    CANVAS_WIDTH / 2 -
    TITLE_PLACEHOLDER_LEFT_OFFSET_INCHES * CANVAS_PX_PER_INCH,
  y: 0,
};

/**
 * Builds the master's default Stanford logo image element, docked to the
 * bottom-right corner. Unlike the legacy per-slide logo, there is one shared
 * copy on the master with a fixed {@link MASTER_LOGO_ELEMENT_ID}.
 *
 * @return A new {@link ImageElement} for the master's logo.
 */
export function createMasterLogoElement(): ImageElement {
  return {
    id: MASTER_LOGO_ELEMENT_ID,
    type: 'image',
    position: DEFAULT_LOGO_POSITION,
    size: DEFAULT_LOGO_SIZE,
    src: DEFAULT_LOGO_SRC,
    alt: 'Stanford University seal',
  };
}

/**
 * Builds the master's default camera placeholder element, docked to the
 * top-right corner (see {@link CAMERA_PLACEHOLDER_POSITION}).
 *
 * @return A new {@link PlaceholderElement} for the master's camera box.
 */
export function createCameraPlaceholderElement(): PlaceholderElement {
  return {
    id: MASTER_CAMERA_ELEMENT_ID,
    type: 'placeholder',
    placeholderKind: 'camera',
    position: CAMERA_PLACEHOLDER_POSITION,
    size: CAMERA_PLACEHOLDER_SIZE,
  };
}

/**
 * Builds the master's default title placeholder element (see
 * {@link DEFAULT_TITLE_PLACEHOLDER_POSITION}), sized like a newly-inserted
 * title text box ({@link DEFAULT_TITLE_SIZE}, {@link DEFAULT_TITLE_FONT_SIZE}).
 * `color`/`fontFamily` are left unset so they fall back to the master's own
 * {@link SlideMasterSchema.elementColor} / the deck-wide default font until an
 * author picks something else in master-edit mode.
 *
 * @return A new {@link PlaceholderElement} for the master's title guide.
 */
export function createTitlePlaceholderElement(): PlaceholderElement {
  return {
    id: MASTER_TITLE_PLACEHOLDER_ELEMENT_ID,
    type: 'placeholder',
    placeholderKind: 'title',
    position: DEFAULT_TITLE_PLACEHOLDER_POSITION,
    size: DEFAULT_TITLE_SIZE,
    fontSize: DEFAULT_TITLE_FONT_SIZE,
  };
}

/**
 * Builds the default slide master seeded onto a new (or pre-master) deck: a
 * black background with the Stanford logo, camera placeholder, and title
 * placeholder in their default positions. See {@link SlideMasterSchema}.
 *
 * @return A new {@link SlideMaster}.
 */
export function createDefaultMaster(): SlideMaster {
  return {
    backgroundColor: DEFAULT_MASTER_BACKGROUND_COLOR,
    elementColor: DEFAULT_MASTER_ELEMENT_COLOR,
    elements: [
      createMasterLogoElement(),
      createCameraPlaceholderElement(),
      createTitlePlaceholderElement(),
    ],
  };
}

/**
 * Ensures a loaded deck has a slide master, migrating pre-master decks on read
 * (the repo's "migrate on read" convention — no `schemaVersion`, no migration
 * script). A deck that already has a `master` is returned unchanged. Otherwise
 * a {@link createDefaultMaster} is attached and each slide is normalized:
 *
 * - Any persisted legacy per-slide logo (id {@link defaultLogoElementId}) is
 *   stripped, since the logo now lives once on the master — otherwise it would
 *   render twice.
 * - A slide that opted out via the deprecated {@link SlideSchema.hideDefaultLogo}
 *   is converted to {@link SlideSchema.hideMaster} so it keeps showing no logo
 *   (it now also hides the camera box, which was a faint guide before).
 *
 * Applied at the single deck-ingest point in `data/useSlideDeck.ts` (and by the
 * editor for freshly-created/loaded decks), so every reader — editor and
 * present mode — sees a master without a Firestore migration.
 *
 * Separately, a deck that already has a master but predates the title
 * placeholder (see {@link PLACEHOLDER_KINDS}) gets one backfilled via
 * {@link createTitlePlaceholderElement} — so "Make Title" (`data/useDeckEditor.ts`'s
 * `makeElementTitle`) always has a placeholder to copy from, without a
 * Firestore migration either.
 *
 * @param deck - The freshly-parsed deck to migrate.
 * @return `deck` unchanged if it already has a master with a title
 *     placeholder; otherwise a copy with the missing piece(s) backfilled.
 */
export function migrateDeckMaster(deck: Deck): Deck {
  if (deck.master === undefined) {
    const slides = deck.slides.map((slide): Slide => {
      const legacyLogoId = defaultLogoElementId(slide.id);
      const elements = slide.elements.filter(
        element => element.id !== legacyLogoId,
      );
      // Drop the deprecated flag from the carried-forward slide; translate it
      // to `hideMaster`. Destructured out so it is never written back to
      // Firestore.
      const {hideDefaultLogo, ...rest} = slide;
      return hideDefaultLogo
        ? {...rest, elements, hideMaster: true}
        : {...rest, elements};
    });
    return {...deck, master: createDefaultMaster(), slides};
  }
  const hasTitlePlaceholder = deck.master.elements.some(
    element =>
      element.type === 'placeholder' && element.placeholderKind === 'title',
  );
  if (hasTitlePlaceholder) {
    return deck;
  }
  return {
    ...deck,
    master: {
      ...deck.master,
      elements: [...deck.master.elements, createTitlePlaceholderElement()],
    },
  };
}

/**
 * Grid, in stored-`fontSize` units, that {@link snapFontSizeNoise} rounds toward.
 * PowerPoint point sizes are whole or half points, and for a clean 16:9/4:3 deck
 * the pptx importer's EMU→pixel product resolves to exactly `1.0`, so a stored
 * size derived from a real point size lands on this same half-unit grid.
 */
const FONT_SIZE_SNAP_STEP = 0.5;

/**
 * Maximum distance from the nearest {@link FONT_SIZE_SNAP_STEP} grid point at
 * which {@link snapFontSizeNoise} treats the offset as floating-point noise and
 * snaps. Kept far tighter than the gap between adjacent real sizes so that
 * genuinely off-grid, deliberately-scaled sizes are left untouched.
 */
const FONT_SIZE_SNAP_EPSILON = 0.02;

/**
 * Snaps a stored font size to the nearest {@link FONT_SIZE_SNAP_STEP} grid point
 * when it is within {@link FONT_SIZE_SNAP_EPSILON} of that grid, otherwise
 * returns it unchanged.
 *
 * The pptx importer's EMU→pixel conversion (`import/pptxParser.ts`) cancels to an
 * exact `×1` only for decks whose dimensions are clean multiples of the canvas;
 * off-ratio decks make it a non-terminating binary fraction a hair off, landing
 * e.g. `36pt` at `35.9987`, which is then persisted verbatim. This snap removes
 * that noise both at import time and, via {@link normalizeDeckFontSizes}, for
 * decks that already have it baked in — while preserving sizes that are
 * legitimately scaled away from the grid.
 *
 * @param value - The stored font size to clean up.
 * @return The snapped value when within the epsilon of a grid point; otherwise `value` unchanged.
 * @throws Never.
 */
export function snapFontSizeNoise(value: number): number {
  const snapped = Math.round(value / FONT_SIZE_SNAP_STEP) * FONT_SIZE_SNAP_STEP;
  return Math.abs(value - snapped) <= FONT_SIZE_SNAP_EPSILON ? snapped : value;
}

/**
 * Snaps an element's `fontSize` (if it has one) via {@link snapFontSizeNoise},
 * returning the same element reference when nothing changes so callers can skip
 * cloning untouched slides. Each element type is handled in its own branch so
 * the spread stays narrowed to a single concrete member (rather than the
 * discriminated union), keeping the result assignable to {@link Element}.
 *
 * @param element - The element to normalize.
 * @return `element` unchanged when it carries no `fontSize` or the size is
 *     already on-grid; otherwise a copy with the snapped `fontSize`.
 * @throws Never.
 */
function normalizeElementFontSize(element: Element): Element {
  switch (element.type) {
    case 'text':
    case 'equation':
    case 'table': {
      const fontSize = snapFontSizeNoise(element.fontSize);
      return fontSize === element.fontSize ? element : {...element, fontSize};
    }
    case 'shape': {
      // `fontSize` is optional on shapes; only rect/circle text carries one.
      if (isNullOrUndefined(element.fontSize)) {
        return element;
      }
      const fontSize = snapFontSizeNoise(element.fontSize);
      return fontSize === element.fontSize ? element : {...element, fontSize};
    }
    default:
      return element;
  }
}

/**
 * One-time migrate-on-read normalization that strips floating-point font-size
 * noise (e.g. `35.9987` for `36pt`) baked into decks imported from `.pptx`
 * before {@link snapFontSizeNoise} was applied at import time. Snaps every
 * `fontSize` across the master and all slides, on both the elements' own sizes
 * and (once written back on the next save) persists the cleaned values.
 *
 * Following the repo's "migrate on read" convention (see {@link migrateDeckMaster}),
 * this runs at the single deck-ingest point in `data/useSlideDeck.ts`. Slides and
 * elements that need no change keep their original reference, so an already-clean
 * deck returns structurally unchanged.
 *
 * @param deck - The freshly-parsed deck to normalize.
 * @return `deck` with all on-grid font sizes snapped; unchanged references where nothing moved.
 * @throws Never.
 */
export function normalizeDeckFontSizes(deck: Deck): Deck {
  const master = isNullOrUndefined(deck.master)
    ? deck.master
    : {
        ...deck.master,
        elements: deck.master.elements.map(normalizeElementFontSize),
      };
  const slides = deck.slides.map(
    (slide): Slide => ({
      ...slide,
      elements: slide.elements.map(normalizeElementFontSize),
    }),
  );
  return {...deck, master, slides};
}
